// Optimized Amplitude loader with lazy loading, error handling, and event queuing

interface QueuedEvent {
  eventName: string;
  properties?: Record<string, unknown>;
  timestamp: number;
}

class AmplitudeLoader {
  private isLoaded = false;
  private isLoading = false;
  private loadPromise: Promise<boolean> | null = null;
  private eventQueue: QueuedEvent[] = [];
  private maxQueueSize = 100;
  private maxQueueAge = 5 * 60 * 1000; // 5 minutes
  private loadTimeout = 10000; // 10 seconds

  constructor() {
    // Clean up old events periodically
    this.startQueueCleanup();
  }

  /**
   * Load Amplitude script asynchronously
   */
  private async loadAmplitudeScript(): Promise<boolean> {
    if (this.isLoaded) return true;
    if (this.isLoading && this.loadPromise) return this.loadPromise;

    this.isLoading = true;
    this.loadPromise = this.performLoad();

    try {
      const result = await this.loadPromise;
      this.isLoaded = result;
      this.isLoading = false;
      
      if (result) {
        this.processEventQueue();
      }
      
      return result;
    } catch (error) {
      console.warn('Amplitude failed to load:', error);
      this.isLoading = false;
      this.isLoaded = false;
      return false;
    }
  }

  private static readonly API_KEY = 'c966be2462878a5b4ae987e8ec139f77';

  /**
   * Perform the actual script loading
   */
  private performLoad(): Promise<boolean> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        resolve(false);
      }, this.loadTimeout);

      if (typeof window !== 'undefined' && window.amplitude) {
        clearTimeout(timeout);
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://cdn.amplitude.com/script/${AmplitudeLoader.API_KEY}.js`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        clearTimeout(timeout);
        try {
          if (window.amplitude) {
            // Add session replay plugin
            if (window.sessionReplay?.plugin) {
              window.amplitude.add(window.sessionReplay.plugin({ sampleRate: 1 }));
            }
            window.amplitude.init(AmplitudeLoader.API_KEY, {
              fetchRemoteConfig: true,
              autocapture: {
                attribution: true,
                fileDownloads: true,
                formInteractions: true,
                pageViews: false,
                sessions: true,
                elementInteractions: true,
                networkTracking: true,
                webVitals: true,
                frustrationInteractions: {
                  thrashedCursor: true,
                  errorClicks: true,
                  deadClicks: true,
                  rageClicks: true,
                },
              },
            });
          }
        } catch (e) {
          console.warn('Amplitude init error:', e);
        }
        setTimeout(() => {
          resolve(typeof window !== 'undefined' && !!window.amplitude);
        }, 100);
      };
      
      script.onerror = () => {
        clearTimeout(timeout);
        resolve(false);
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Track an event, loading Amplitude if needed
   */
  async track(eventName: string, properties?: Record<string, unknown>): Promise<void> {
    // If already loaded, track immediately
    if (this.isLoaded && typeof window !== 'undefined' && window.amplitude) {
      try {
        window.amplitude.track(eventName, properties);
        return;
      } catch (error) {
        console.warn('Amplitude tracking error:', error);
      }
    }

    // Queue the event
    this.queueEvent(eventName, properties);

    // Try to load Amplitude if not already loading
    if (!this.isLoading) {
      this.loadAmplitudeScript().catch(() => {
        // Error already handled in loadAmplitudeScript
      });
    }
  }

  /**
   * Queue an event for later processing
   */
  private queueEvent(eventName: string, properties?: Record<string, unknown>): void {
    // Remove old events if queue is getting too large
    if (this.eventQueue.length >= this.maxQueueSize) {
      this.eventQueue = this.eventQueue.slice(-this.maxQueueSize / 2);
    }

    this.eventQueue.push({
      eventName,
      properties,
      timestamp: Date.now(),
    });
  }

  /**
   * Process all queued events
   */
  private processEventQueue(): void {
    if (typeof window === 'undefined' || !window.amplitude) return;

    const eventsToProcess = [...this.eventQueue];
    this.eventQueue = [];

    eventsToProcess.forEach(({ eventName, properties }) => {
      try {
        window.amplitude.track(eventName, properties);
      } catch (error) {
        console.warn('Error processing queued Amplitude event:', error);
      }
    });
  }

  /**
   * Clean up old events from queue
   */
  private startQueueCleanup(): void {
    setInterval(() => {
      const now = Date.now();
      this.eventQueue = this.eventQueue.filter(
        event => now - event.timestamp < this.maxQueueAge
      );
    }, 60000); // Clean up every minute
  }

  /**
   * Check if Amplitude is ready
   */
  isReady(): boolean {
    return this.isLoaded && typeof window !== 'undefined' && !!window.amplitude;
  }

  /**
   * Get loading status
   */
  getStatus(): { isLoaded: boolean; isLoading: boolean; queueSize: number } {
    return {
      isLoaded: this.isLoaded,
      isLoading: this.isLoading,
      queueSize: this.eventQueue.length,
    };
  }

  /**
   * Force reload Amplitude (useful for error recovery)
   */
  async reload(): Promise<boolean> {
    this.isLoaded = false;
    this.isLoading = false;
    this.loadPromise = null;
    return this.loadAmplitudeScript();
  }
}

// Create singleton instance
export const amplitudeLoader = new AmplitudeLoader();

// Export convenience functions
export const trackAmplitudeEvent = (eventName: string, properties?: Record<string, unknown>) => {
  return amplitudeLoader.track(eventName, properties);
};

export const isAmplitudeReady = () => amplitudeLoader.isReady();

export const getAmplitudeStatus = () => amplitudeLoader.getStatus();

export const reloadAmplitude = () => amplitudeLoader.reload();
